import { useMemo } from "react";

import { DateTime } from "luxon";

import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useCreateAlertHandler } from "../../../../Pages/CloudAnalytics/alerts/hooks";
import { type AnalyticsAlertWithRef } from "../../../../Pages/CloudAnalytics/alerts/types";
import ActiveItemsCard from "../ActiveItemsCard";
import { LoadingCard } from "../LoadingCard";
import { CreateItemCard } from "./CreateItemCard";

export const ActiveAlertsCard = ({ alertList, loading }: { alertList: AnalyticsAlertWithRef[]; loading: boolean }) => {
  const filteredAlerts = useMemo(() => alertList.filter((alert) => alert.data.isValid), [alertList]);
  const { customer } = useCustomerContext();

  const handleNewAlert = useCreateAlertHandler({
    mixpanelEventName: "analytics.alerts.new",
    baseUrl: `/customers/${customer.id}/monitor/alerts`,
    prevPageUrl: `/customers/${customer.id}/home`,
  });

  const thirtyDaysAgo = DateTime.now().minus({ days: 30 }).toJSDate();
  const oneDayAgo = DateTime.now().minus({ days: 1 }).toJSDate();

  const triggeredAlerts30DaysAgo = useMemo(
    () =>
      filteredAlerts?.filter(
        (alert) => alert.data.timeLastAlerted && alert.data.timeLastAlerted.toDate() > thirtyDaysAgo
      ),
    [filteredAlerts, thirtyDaysAgo]
  );

  const triggeredAlerts1DayAgo = useMemo(
    () =>
      filteredAlerts?.filter((alert) => alert.data.timeLastAlerted && alert.data.timeLastAlerted.toDate() > oneDayAgo),
    [filteredAlerts, oneDayAgo]
  );

  if (loading) {
    return <LoadingCard title="Active alerts" />;
  }

  if (filteredAlerts?.length === 0) {
    return (
      <CreateItemCard
        title="Active alerts"
        buttonLabel="Create alert"
        createMsg="You haven't set up any alerts yet"
        onButtonClick={handleNewAlert}
        buttonDataCy="create-alert-btn"
      />
    );
  }

  return (
    <ActiveItemsCard
      title="Active alerts"
      value={filteredAlerts?.length ?? 0}
      cloudCostsAtRisk={triggeredAlerts30DaysAgo?.length ?? 0}
      dataIcon="trending down"
      trend={triggeredAlerts1DayAgo?.length ?? 0}
      trendColor="error.main"
      trendDate="yesterday"
      riskLabel="Alerts triggered in the last 30 days"
      riskIsNumber={true}
      buttonLink="./monitor/alerts"
    />
  );
};
